/**
 * Validação do relato no servidor.
 *
 * Duas políticas diferentes de propósito:
 *
 * - **O texto do aluno** é recusado quando está vazio ou grande demais. É o
 *   único campo que o servidor exige, e o erro volta para a tela.
 * - **O diagnóstico** nunca recusa nada: campo torto é truncado ou cai para o
 *   vazio. Um `userAgent` esquisito não pode fazer o relato inteiro sumir —
 *   o texto do aluno vale mais do que qualquer linha do anexo.
 */

import { z } from "zod"

import {
  MESSAGE_MAX_LENGTH,
  PATH_MAX_LENGTH,
  ROUTE_TRAIL_LIMIT,
  SESSION_ERROR_LIMIT,
  STACK_MAX_LENGTH,
} from "@/lib/analytics/session-diagnostics"

import {
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MIN_LENGTH,
  EXPECTED_MAX_LENGTH,
  SHORT_FIELD_MAX_LENGTH,
  URL_MAX_LENGTH,
  USER_AGENT_MAX_LENGTH,
} from "./constants"

/** String tolerante: corta no limite em vez de reprovar. */
function clamped(max: number) {
  return z
    .string()
    .transform((value) => value.slice(0, max))
    .catch("")
}

const TIMESTAMP_MAX_LENGTH = 40

const sessionErrorSchema = z.object({
  source: z.enum(["error", "unhandledrejection"]).catch("error"),
  message: clamped(MESSAGE_MAX_LENGTH),
  stack: z
    .string()
    .transform((value) => value.slice(0, STACK_MAX_LENGTH))
    .nullable()
    .catch(null),
  firstAt: clamped(TIMESTAMP_MAX_LENGTH),
  lastAt: clamped(TIMESTAMP_MAX_LENGTH),
  count: z.coerce.number().int().min(1).max(9999).catch(1),
})

const routeVisitSchema = z.object({
  path: clamped(PATH_MAX_LENGTH),
  at: clamped(TIMESTAMP_MAX_LENGTH),
})

export const EMPTY_DIAGNOSTICS = {
  url: "",
  routes: [],
  errors: [],
  userAgent: "",
  viewport: "",
  timezone: "",
  language: "",
} as const

const diagnosticsSchema = z
  .object({
    url: clamped(URL_MAX_LENGTH),
    // Fica com as mais recentes: o fim da trilha é o que explica a tela atual.
    routes: z
      .array(routeVisitSchema)
      .transform((routes) => routes.slice(-ROUTE_TRAIL_LIMIT))
      .catch([]),
    errors: z
      .array(sessionErrorSchema)
      .transform((errors) => errors.slice(-SESSION_ERROR_LIMIT))
      .catch([]),
    userAgent: clamped(USER_AGENT_MAX_LENGTH),
    viewport: clamped(SHORT_FIELD_MAX_LENGTH),
    timezone: clamped(SHORT_FIELD_MAX_LENGTH),
    language: clamped(SHORT_FIELD_MAX_LENGTH),
  })
  .catch(() => ({ ...EMPTY_DIAGNOSTICS, routes: [], errors: [] }))

export const bugReportSchema = z.object({
  description: z
    .string("Conte o que aconteceu.")
    .trim()
    .min(DESCRIPTION_MIN_LENGTH, "Conte um pouco mais sobre o que aconteceu.")
    .max(
      DESCRIPTION_MAX_LENGTH,
      `Máximo de ${DESCRIPTION_MAX_LENGTH} caracteres.`
    ),
  expected: z
    .string()
    .trim()
    .max(EXPECTED_MAX_LENGTH, `Máximo de ${EXPECTED_MAX_LENGTH} caracteres.`)
    .optional(),
  diagnostics: diagnosticsSchema,
})

export type ValidatedBugReport = z.infer<typeof bugReportSchema>
